import { lazy } from 'react'

/**
 * Configuration for retry behavior
 */
const RETRY_CONFIG = {
    maxAttempts: 3,
    baseDelay: 500, // 500ms base delay
    backoffMultiplier: 3, // Exponential backoff: 500ms, 1500ms, 4500ms
    retryableErrors: [
        'Failed to fetch',
        'Loading chunk',
        'Loading CSS chunk',
        'NetworkError',
        'TypeError: Failed to fetch',
        'ChunkLoadError',
        'ERR_INTERNET_DISCONNECTED',
        'ERR_NETWORK_CHANGED',
        'Failed to fetch dynamically imported module',
        'ERR_NAME_NOT_RESOLVED',
        'ERR_CONNECTION_REFUSED',
    ],
    // Errors that indicate deployment/version mismatch (should trigger page reload)
    // These patterns are specific to chunk loading failures, not general API 404s
    deploymentErrors: [
        'Failed to fetch dynamically imported module',
        'Loading chunk',
        'Loading CSS chunk',
        'The requested module',
        'Cannot resolve module',
        'ChunkLoadError',
    ],
}

/**
 * Determines if an error is retryable based on common network failure patterns
 * @param {Error} error - The error to check
 * @returns {boolean} - Whether the error should trigger a retry
 */
const isRetryableError = (error) => {
    if (!error) return false

    const errorMessage = error.message || error.toString()
    const errorName = error.name || ''

    return RETRY_CONFIG.retryableErrors.some((pattern) => errorMessage.includes(pattern) || errorName.includes(pattern))
}

/**
 * Determines if an error indicates a deployment/version mismatch (e.g., 404 on chunk)
 * @param {Error} error - The error to check
 * @returns {boolean} - Whether the error indicates a deployment issue
 */
const isDeploymentError = (error) => {
    if (!error) return false

    const errorMessage = error.message || error.toString()
    const errorName = error.name || ''

    return RETRY_CONFIG.deploymentErrors.some(
        (pattern) => errorMessage.includes(pattern) || errorName.includes(pattern)
    )
}

/**
 * Triggers a page reload with cache busting to recover from deployment errors
 * @param {string} componentName - Name of the component that failed to load
 * @param {Error} error - The error that triggered the reload
 */
const triggerDeploymentRecovery = (componentName, error) => {
    // Prevent infinite reload loops by checking if we've recently attempted a reload
    const reloadKey = 'qm_deployment_reload_attempt'
    const reloadAttempts = sessionStorage.getItem(reloadKey)
    const now = Date.now()
    const maxReloads = 2 // Maximum reloads within the time window
    const reloadWindow = 5 * 60 * 1000 // 5 minutes

    if (reloadAttempts) {
        try {
            const { count, timestamp } = JSON.parse(reloadAttempts)
            if (now - timestamp < reloadWindow) {
                if (count >= maxReloads) {
                    console.warn(
                        `Maximum reload attempts (${maxReloads}) reached within ${reloadWindow / 1000}s. Skipping reload to prevent infinite loop.`
                    )
                    return // Don't reload to prevent infinite loops
                }
            }
        } catch (e) {
            // Invalid JSON, continue with reload
        }
    }

    // Update reload attempt counter
    try {
        const attempts = reloadAttempts ? JSON.parse(reloadAttempts) : { count: 0, timestamp: now }

        if (now - attempts.timestamp > reloadWindow) {
            // Reset counter if outside time window
            attempts.count = 1
            attempts.timestamp = now
        } else {
            // Increment within time window
            attempts.count += 1
        }

        sessionStorage.setItem(reloadKey, JSON.stringify(attempts))
    } catch (e) {
        console.warn('Failed to update reload counter:', e)
    }

    console.warn(
        `Deployment-related chunk loading error detected for ${componentName}. Reloading page to get fresh chunks.`,
        error
    )

    // Track deployment recovery attempt
    if (window.appInsights) {
        window.appInsights.trackEvent({
            name: 'DeploymentRecoveryTriggered',
            properties: {
                componentName,
                errorMessage: error?.message || '',
                errorName: error?.name || '',
                userAgent: navigator.userAgent,
                url: window.location.href,
            },
        })
    }

    // Clear relevant caches before reload
    if ('caches' in window) {
        try {
            caches
                .keys()
                .then((names) => {
                    names.forEach((name) => {
                        if (name.includes('vite') || name.includes('workbox') || name.includes('runtime')) {
                            caches.delete(name)
                        }
                    })
                })
                .catch(() => {
                    // Ignore cache clearing errors - proceed with reload anyway
                })
                .finally(() => {
                    // Reload page to get fresh chunks
                    window.location.reload()
                })
        } catch {
            // Fallback: simple reload
            window.location.reload()
        }
    } else {
        // No cache API available, just reload
        window.location.reload()
    }
}

/**
 * Delays execution by the specified number of milliseconds
 * @param {number} ms - Milliseconds to delay
 * @returns {Promise} - Promise that resolves after the delay
 */
const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms))

/**
 * Calculates the delay for a retry attempt using exponential backoff
 * @param {number} attemptNumber - The current attempt number (1-based)
 * @returns {number} - Delay in milliseconds
 */
const calculateRetryDelay = (attemptNumber) => {
    return RETRY_CONFIG.baseDelay * Math.pow(RETRY_CONFIG.backoffMultiplier, attemptNumber - 1)
}

/**
 * Tracks retry attempts for Application Insights monitoring
 * @param {string} componentName - Name of the component being loaded
 * @param {number} attemptNumber - Current attempt number
 * @param {Error} error - The error that triggered the retry
 * @param {boolean} isSuccess - Whether this attempt succeeded
 */
const trackRetryAttempt = (componentName, attemptNumber, error, isSuccess = false) => {
    if (window.appInsights) {
        const eventName = isSuccess ? 'DynamicImportRetrySuccess' : 'DynamicImportRetryAttempt'

        window.appInsights.trackEvent({
            name: eventName,
            properties: {
                componentName,
                attemptNumber,
                errorMessage: error?.message || '',
                errorName: error?.name || '',
                isRetryable: isRetryableError(error),
                retryDelay: isSuccess ? 0 : calculateRetryDelay(attemptNumber + 1),
            },
        })
    }
}

/**
 * Attempts to import a module with retry logic for network failures
 * @param {Function} importFunction - The dynamic import function to execute
 * @param {string} componentName - Name of the component for logging (optional)
 * @returns {Promise} - Promise that resolves to the imported module
 */
const importWithRetry = async (importFunction, componentName = 'Unknown') => {
    let lastError = null

    for (let attempt = 1; attempt <= RETRY_CONFIG.maxAttempts; attempt++) {
        try {
            const module = await importFunction()

            // Track successful retry if this wasn't the first attempt
            if (attempt > 1) {
                trackRetryAttempt(componentName, attempt, lastError, true)
            }

            return module
        } catch (error) {
            lastError = error

            // Track the retry attempt
            trackRetryAttempt(componentName, attempt, error, false)

            // Check if this is a deployment-related error that should be deferred to error boundary
            if (isDeploymentError(error)) {
                console.warn(`Deployment error detected on attempt ${attempt} for ${componentName}:`, error.message)

                // For deployment errors, don't retry or trigger immediate recovery
                // Instead, let the error boundary handle automatic reload attempts
                if (attempt === 1) {
                    // Track deployment error - let error boundary handle recovery
                    if (window.appInsights) {
                        window.appInsights.trackEvent({
                            name: 'DeploymentErrorDetected',
                            properties: {
                                componentName,
                                attemptNumber: attempt,
                                errorMessage: error?.message || '',
                                errorName: error?.name || '',
                                willDeferToErrorBoundary: true,
                            },
                        })
                    }

                    // Create deployment error for error boundary to handle
                    const deploymentError = new Error(`Deployment mismatch detected: ${error.message}`)
                    deploymentError.originalError = error
                    deploymentError.attemptCount = attempt
                    deploymentError.componentName = componentName
                    deploymentError.name = 'DeploymentMismatchError'
                    deploymentError.requiresAutomaticReload = true
                    throw deploymentError
                }
            }

            // If this is the last attempt or error is not retryable, throw the error
            if (attempt === RETRY_CONFIG.maxAttempts || !isRetryableError(error)) {
                // Add additional context to the error for better debugging
                const enhancedError = new Error(`Dynamic import failed after ${attempt} attempts: ${error.message}`)
                enhancedError.originalError = error
                enhancedError.attemptCount = attempt
                enhancedError.componentName = componentName
                enhancedError.name = 'DynamicImportError'

                throw enhancedError
            }

            // Wait before retrying with exponential backoff
            const retryDelay = calculateRetryDelay(attempt + 1)
            await delay(retryDelay)
        }
    }
}

/**
 * Creates a lazy-loaded React component with automatic retry logic for failed imports
 * @param {Function} importFunction - Function that returns a dynamic import promise
 * @param {string} componentName - Optional name for the component (for logging)
 * @returns {React.LazyExoticComponent} - Lazy component with retry logic
 */
const lazyWithRetry = (importFunction, componentName) => {
    return lazy(() => importWithRetry(importFunction, componentName))
}

export { importWithRetry, isDeploymentError, isRetryableError, lazyWithRetry, RETRY_CONFIG, triggerDeploymentRecovery }
export default lazyWithRetry
